import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { accessKeyErrorMessage } from "@/hooks/use-access-keys";
import { fetchSubdesks, subdesksQueryKey } from "@/hooks/use-desk-migration";
import { backofficeClient } from "@/lib/api/backoffice-client";
import type { ExistingDesk } from "@/lib/desk-suggestions/existing-desks";
import type {
  DeskSuggestionListResponse,
  DeskSuggestionRun,
  MarkDeskSuggestionAppliedRequest,
  ProvisionDeskRequest,
  ProvisionDeskResponse,
  ProvisionOutcome,
  ProvisionedSubdeskResult,
} from "@/lib/types/desk-suggestions";

const SUGGESTIONS_PATH =
  "/proxy/desk-designer/api/v1/internal/desk-suggestions";
const PROVISION_PATH =
  "/proxy/service-desk/_internal/backoffice/desks/provision";

export const deskSuggestionsQueryKey = [
  "desk-designer",
  "desk-suggestions",
] as const;

/** Runs still generating are polled until they settle. */
const IN_PROGRESS_POLL_MS = 5000;

function isInProgress(run: DeskSuggestionRun): boolean {
  return run.status === "pending" || run.status === "running";
}

export function useDeskSuggestionRuns(tenantId: string) {
  return useQuery({
    queryKey: [...deskSuggestionsQueryKey, tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<DeskSuggestionListResponse>(
        SUGGESTIONS_PATH,
        {
          params: { tenant_id: tenantId },
        },
      );
      return data.items;
    },
    enabled: Boolean(tenantId),
    refetchInterval: (query) =>
      query.state.data?.some(isInProgress) ? IN_PROGRESS_POLL_MS : false,
  });
}

export function useDeskSuggestionRun(tenantId: string, runId: string) {
  return useQuery({
    queryKey: [...deskSuggestionsQueryKey, tenantId, runId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<DeskSuggestionRun>(
        `${SUGGESTIONS_PATH}/${runId}`,
        {
          params: { tenant_id: tenantId },
        },
      );
      return data;
    },
    enabled: Boolean(tenantId) && Boolean(runId),
    refetchInterval: (query) =>
      query.state.data && isInProgress(query.state.data)
        ? IN_PROGRESS_POLL_MS
        : false,
  });
}

/** The subdesks of each of `deskIds`, in the same order. */
export function useExistingSubdesks(tenantId: string, deskIds: string[]) {
  return useQueries({
    queries: deskIds.map((deskId) => ({
      queryKey: subdesksQueryKey(tenantId, deskId),
      queryFn: () => fetchSubdesks(tenantId, deskId),
      enabled: Boolean(tenantId),
    })),
  });
}

export interface DeskCreateResult {
  /** The suggested desk's name, which is what the run records as applied. */
  name: string;
  /** The tenant desk created or matched, when the call succeeded. */
  desk: ExistingDesk | null;
  /**
   * Submitted to add subdesks to an existing desk. Such a call should never
   * create the desk; outcome "created" means the desk was not found anymore.
   */
  synced: boolean;
  outcome: ProvisionOutcome;
  error: string | null;
  subdesks: ProvisionedSubdeskResult[];
}

export interface CreateSuggestedDesksResult {
  desks: DeskCreateResult[];
  /** Set when the desks were created but recording them on the run failed. */
  markAppliedError: string | null;
}

export interface SuggestedDeskSubmission {
  /** The suggested desk's name, recorded on the run once applied. */
  suggestionName: string;
  /** Adds subdesks to an existing desk instead of creating one. */
  synced: boolean;
  request: ProvisionDeskRequest;
}

interface CreateSuggestedDesksInput {
  tenantId: string;
  runId: string;
  desks: SuggestedDeskSubmission[];
}

/** A desk this call created, or an existing desk it added subdesks to. */
function wasApplied(result: DeskCreateResult): boolean {
  return (
    result.outcome === "created" ||
    (result.outcome === "existing" &&
      result.subdesks.some((s) => s.outcome === "created"))
  );
}

/**
 * Provisions each desk in turn (one failure does not stop the rest), then
 * records on the run the desks this call created or added subdesks to.
 */
export function useCreateSuggestedDesks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      runId,
      desks,
    }: CreateSuggestedDesksInput): Promise<CreateSuggestedDesksResult> => {
      const results: DeskCreateResult[] = [];
      for (const { suggestionName, synced, request } of desks) {
        try {
          const { data } = await backofficeClient.post<ProvisionDeskResponse>(
            PROVISION_PATH,
            request,
            {
              params: { tenant_id: tenantId },
            },
          );
          results.push({
            name: suggestionName,
            desk: {
              id: data.desk.id,
              name: data.desk.name,
              template_id: data.desk.template_id ?? null,
              enabled: data.desk.enabled,
            },
            synced,
            outcome: data.outcome,
            error: null,
            subdesks: data.subdesks,
          });
        } catch (error: unknown) {
          results.push({
            name: suggestionName,
            desk: null,
            synced,
            outcome: "failed",
            error: accessKeyErrorMessage(error, "Failed to create desk"),
            subdesks: [],
          });
        }
      }

      const appliedNames = results.filter(wasApplied).map((r) => r.name);
      let markAppliedError: string | null = null;
      if (appliedNames.length > 0) {
        const body: MarkDeskSuggestionAppliedRequest = {
          created_desk_names: appliedNames,
        };
        try {
          await backofficeClient.post(
            `${SUGGESTIONS_PATH}/${runId}/applied`,
            body,
            {
              params: { tenant_id: tenantId },
            },
          );
        } catch (error: unknown) {
          markAppliedError = accessKeyErrorMessage(
            error,
            "Failed to mark the run applied",
          );
        }
      }

      return { desks: results, markAppliedError };
    },
    onSettled: async (_data, _error, { tenantId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["service-desk", "desks", tenantId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["service-desk", "subdesks", tenantId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...deskSuggestionsQueryKey, tenantId],
        }),
      ]);
    },
  });
}
